// Sistema de música inmersiva — Web Audio API.
//
// Filosofía cinematográfica:
//   - No competir: volumen base bajo (~35% default), nunca al frente.
//   - El silencio respira: 4-18s entre loops (configurable per track).
//   - Crossfade 2s entre mapas — nunca un corte abrupto.
//   - Ducking en eventos clave: muerte, level up, susurro, boss combat.
//   - Tab inactivo → fade-out 1s; volver → fade-in 1.5s.
//   - Pseudo-random estable por bioma (hash mapNum), no random aleatorio.
//
// Arquitectura del grafo de audio:
//
//     AudioBufferSourceNode (currentTrack)
//                ↓
//           trackGain (per-track gain, para crossfade)
//                ↓
//           duckGain (envelope para eventos)
//                ↓
//           masterGain (volumen del usuario, 0-1)
//                ↓
//           destination (speakers)
//
// El track cambia por crossfade: levantamos un AudioBufferSourceNode
// nuevo en paralelo con su propio trackGain, fade-in el nuevo + fade-out
// del viejo en simultáneo, después destruimos el viejo.
//
// El silencio entre loops se logra usando setTimeout en lugar del loop
// nativo del AudioBufferSourceNode — al `onended` esperamos N segundos
// random y re-arrancamos. Así el silencio NO está bakeado en el archivo,
// es dinámico per loop.

const CROSSFADE_MS = 2000;
const FADE_IN_MS = 1500;
const FADE_OUT_MS = 1000;
const TAB_FADE_MS = 1000;
const DEFAULT_VOLUME = 0.35;

// Tracks que NO loopean (intro_historica). Si querés sumar más, marcarlos
// con loopSilence: [0, 0] en musicMap.json.
function isOneshot(trackConfig) {
    return (
        trackConfig &&
        Array.isArray(trackConfig.loopSilence) &&
        trackConfig.loopSilence[0] === 0 &&
        trackConfig.loopSilence[1] === 0
    );
}

// Hash determinístico simple — mapNum → índice de track en rotación.
// Estable por mapa (siempre el mismo bosque suena el mismo track), distinto
// entre mapas vecinos (mapa 70 vs 71 puede sonar distinto).
function hashMapToIndex(mapNum, arrLen) {
    if (arrLen <= 1) return 0;
    let h = 2166136261;
    const s = String(mapNum || 0);
    for (let i = 0; i < s.length; i++) {
        h = (h ^ s.charCodeAt(i)) * 16777619;
    }
    return Math.abs(h >>> 0) % arrLen;
}

class MusicEngine {
    constructor() {
        this.config = null;          // musicMap.json
        this.ctx = null;             // AudioContext
        this.masterGain = null;      // Gain del usuario (slider)
        this.duckGain = null;        // Gain de eventos puntuales
        this.bufferCache = {};       // trackId → AudioBuffer (LRU informal)
        this.currentSource = null;   // AudioBufferSourceNode actual
        this.currentTrackGain = null;
        this.currentTrackId = null;
        this.currentLoopTimeout = null;
        this.targetMapNumber = null;
        this.volume = DEFAULT_VOLUME;
        this.enabled = true;
        this.tabHidden = false;
        this._loadingPromises = {};  // dedup de fetch en flight
        this._currentTrackName = ""; // label para UI
        this._listeners = new Set(); // suscriptores al "currentTrackName" change
    }

    // Init lazy — necesita un user gesture para el AudioContext (Chrome).
    // Llamar desde un click handler (typ. el toggle de música o el primer
    // map change post-login).
    async init() {
        if (this.ctx) return;
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) {
                console.warn("[music] Web Audio API no disponible");
                return;
            }
            this.ctx = new Ctx();
            this.masterGain = this.ctx.createGain();
            this.duckGain = this.ctx.createGain();
            this.masterGain.gain.value = this.enabled ? this.volume : 0;
            this.duckGain.gain.value = 1;
            this.duckGain.connect(this.masterGain);
            this.masterGain.connect(this.ctx.destination);

            const resp = await fetch("/static/audio/musicMap.json");
            this.config = await resp.json();

            // Hooks de visibilidad — fade-out al tab inactivo.
            if (typeof document !== "undefined") {
                document.addEventListener("visibilitychange", this._onVis);
            }
        } catch (e) {
            console.warn("[music] init error:", e && e.message);
        }
    }

    _onVis = () => {
        if (!this.ctx) return;
        const hidden = document.hidden;
        if (hidden === this.tabHidden) return;
        this.tabHidden = hidden;
        if (hidden) {
            this._fadeMaster(0, TAB_FADE_MS);
        } else {
            this._fadeMaster(this.enabled ? this.volume : 0, TAB_FADE_MS + 500);
        }
    };

    // v2026-06-24: cleanup completo para componentWillUnmount de Play.
    // Sin esto, el visibilitychange listener queda colgado en document
    // apuntando a un singleton huérfano + el setTimeout del loop sobrevive
    // a la desmontada → leak real cada HMR cycle + leftover SFX engine
    // en memoria. La siguiente vez que el user logue al juego, init() vuelve
    // a crear todo gracias al `if (this.ctx) return;` guard que ahora
    // funciona porque destroy() puso ctx=null.
    destroy() {
        try {
            if (typeof document !== "undefined") {
                document.removeEventListener("visibilitychange", this._onVis);
            }
            if (this.currentLoopTimeout) {
                clearTimeout(this.currentLoopTimeout);
                this.currentLoopTimeout = null;
            }
            // Stop el track actual silenciosamente (sin fade — la page se
            // está desmontando, no hay viewer audible).
            if (this.currentSource) {
                try { this.currentSource.stop(); } catch (e) {}
                try { this.currentSource.disconnect(); } catch (e) {}
                this.currentSource = null;
            }
            if (this.currentTrackGain) {
                try { this.currentTrackGain.disconnect(); } catch (e) {}
                this.currentTrackGain = null;
            }
            if (this.ctx) {
                try { this.ctx.close(); } catch (e) {}
            }
            this.ctx = null;
            this.masterGain = null;
            this.duckGain = null;
            this.bufferCache = {};        // libera ~50-300MB de AudioBuffers decoded
            this._loadingPromises = {};
            this.currentTrackId = null;
            this.targetMapNumber = null;
            this._listeners.clear();
        } catch (e) {
            console.warn("[music] destroy error:", e && e.message);
        }
    }

    // Suscripción al cambio del nombre del track actual — la UI lo
    // consume para mostrar "♪ Ullathorpe" en la sección de Audio.
    onTrackChange(cb) {
        this._listeners.add(cb);
        return () => this._listeners.delete(cb);
    }

    getCurrentTrackName() {
        return this._currentTrackName;
    }

    _emitTrackChange() {
        for (const cb of this._listeners) {
            try { cb(this._currentTrackName); } catch (e) {}
        }
    }

    setVolume(v01) {
        this.volume = Math.max(0, Math.min(1, v01));
        if (!this.ctx) return;
        if (this.enabled && !this.tabHidden) {
            this._fadeMaster(this.volume, 200);
        }
    }

    setEnabled(on) {
        this.enabled = !!on;
        if (!this.ctx) return;
        this._fadeMaster(this.enabled ? this.volume : 0, 300);
    }

    _fadeMaster(target, durationMs) {
        if (!this.ctx) return;
        const now = this.ctx.currentTime;
        const dur = durationMs / 1000;
        const g = this.masterGain.gain;
        try {
            const current = g.value;
            g.cancelScheduledValues(now);
            g.setValueAtTime(current, now);
            g.linearRampToValueAtTime(target, now + dur);
        } catch (e) {}
    }

    // Resuelve mapNumber → trackId. Puede devolver null (mapa sin música
    // = silencio intencional).
    resolveTrackForMap(mapNumber) {
        if (!this.config || !mapNumber) return null;
        const entry = this.config.maps[String(mapNumber)];
        if (!entry) return null;
        // Si es un rotation, hashear mapNumber para elegir track estable.
        const rot = this.config.rotations && this.config.rotations[entry];
        if (Array.isArray(rot) && rot.length > 0) {
            return rot[hashMapToIndex(mapNumber, rot.length)];
        }
        return entry;
    }

    // Setea el mapa actual. Es idempotente — si el track resultado es el
    // mismo, no hace nada. Si es distinto, crossfade.
    async setMap(mapNumber) {
        if (!this.ctx) return;
        if (this.targetMapNumber === mapNumber) return;
        this.targetMapNumber = mapNumber;
        const trackId = this.resolveTrackForMap(mapNumber);
        await this._transitionToTrack(trackId);
    }

    // Trigger explícito (intro). El intro arranca al login con el track
    // 'intro_historica' que es one-shot — no loopea. Después de que termina,
    // el setMap del mapa actual toma el control.
    async playIntro() {
        if (!this.ctx) return;
        await this._transitionToTrack("intro_historica", { fadeInMs: 3000 });
    }

    // Ducking: baja el volumen N% por M segundos y vuelve a subir suave.
    // amount = factor (0.3 = -70%), duration = ms total del envelope.
    duck(amount, durationMs) {
        if (!this.ctx) return;
        const target = Math.max(0, Math.min(1, amount));
        const now = this.ctx.currentTime;
        const total = durationMs / 1000;
        const attack = 0.15;      // 150ms fade-down
        const release = total - attack;
        const g = this.duckGain.gain;
        try {
            const cur = g.value;
            g.cancelScheduledValues(now);
            g.setValueAtTime(cur, now);
            g.linearRampToValueAtTime(target, now + attack);
            g.linearRampToValueAtTime(1, now + attack + release);
        } catch (e) {}
    }

    async _loadBuffer(trackId) {
        if (this.bufferCache[trackId]) return this.bufferCache[trackId];
        if (this._loadingPromises[trackId]) return this._loadingPromises[trackId];
        const trackConfig = this.config.tracks[trackId];
        if (!trackConfig) return null;
        const url = "/static/audio/musica/" + trackConfig.file;
        const p = (async () => {
            try {
                const resp = await fetch(url);
                const arr = await resp.arrayBuffer();
                const buf = await this.ctx.decodeAudioData(arr);
                this.bufferCache[trackId] = buf;
                return buf;
            } catch (e) {
                console.warn("[music] failed to load", trackId, e && e.message);
                return null;
            } finally {
                delete this._loadingPromises[trackId];
            }
        })();
        this._loadingPromises[trackId] = p;
        return p;
    }

    async _transitionToTrack(trackId, opts) {
        opts = opts || {};
        const fadeInMs = opts.fadeInMs || FADE_IN_MS;

        // No-op si ya está sonando el mismo track.
        if (trackId === this.currentTrackId) return;

        // Si trackId es null → fade out, silencio.
        if (!trackId) {
            this._fadeOutCurrent(FADE_OUT_MS);
            this.currentTrackId = null;
            this._currentTrackName = "";
            this._emitTrackChange();
            return;
        }

        const buf = await this._loadBuffer(trackId);
        if (!buf) {
            console.warn("[music] no buffer for", trackId);
            return;
        }
        // Si el setMap fue interrumpido mientras cargábamos, abortar.
        if (this.targetMapNumber !== null) {
            const expected = this.resolveTrackForMap(this.targetMapNumber);
            // Permitir explicit intro/playIntro que es trackId == "intro_historica"
            if (trackId !== "intro_historica" && expected !== trackId) {
                return;
            }
        }

        const trackConfig = this.config.tracks[trackId];
        this._fadeOutCurrent(CROSSFADE_MS);
        this._startTrack(trackId, buf, trackConfig, fadeInMs);
    }

    _startTrack(trackId, buffer, trackConfig, fadeInMs) {
        const source = this.ctx.createBufferSource();
        const gain = this.ctx.createGain();
        source.buffer = buffer;
        source.loop = false; // controlamos el loop manualmente con silencio
        source.connect(gain);
        gain.connect(this.duckGain);

        // Per-track volume override (default 1.0 = normal). Útil para tracks
        // que graban muy fuerte (mastering inconsistente) o que el diseño
        // pide más sutiles que el resto (ej. ciudad inicial Ullathorpe a 0.15
        // — música casi imperceptible para no fatigar al newbie).
        const trackVol =
            typeof trackConfig.volume === "number" ? trackConfig.volume : 1;

        const now = this.ctx.currentTime;
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(trackVol, now + fadeInMs / 1000);

        source.onended = () => {
            // Si fuimos reemplazados, no relanzar.
            if (this.currentSource !== source) return;
            if (isOneshot(trackConfig)) {
                this.currentTrackId = null;
                this._currentTrackName = "";
                this._emitTrackChange();
                return;
            }
            const [smin, smax] = trackConfig.loopSilence || [4, 12];
            const silenceMs = (smin + Math.random() * (smax - smin)) * 1000;
            this.currentLoopTimeout = setTimeout(() => {
                this.currentLoopTimeout = null;
                if (this.currentTrackId !== trackId) return;
                // Re-arrancar el mismo track (sin crossfade — es un loop, no transition).
                this._startTrack(trackId, buffer, trackConfig, 600);
            }, silenceMs);
        };

        try {
            source.start();
        } catch (e) {}

        this.currentSource = source;
        this.currentTrackGain = gain;
        this.currentTrackId = trackId;
        // Nombre legible del trackId — convierte snake_case a Title Case.
        this._currentTrackName = trackId
            .split("_")
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
        this._emitTrackChange();
    }

    _fadeOutCurrent(durationMs) {
        if (this.currentLoopTimeout) {
            clearTimeout(this.currentLoopTimeout);
            this.currentLoopTimeout = null;
        }
        const source = this.currentSource;
        const gain = this.currentTrackGain;
        this.currentSource = null;
        this.currentTrackGain = null;
        if (!source || !gain) return;
        const now = this.ctx.currentTime;
        const dur = durationMs / 1000;
        try {
            const cur = gain.gain.value;
            gain.gain.cancelScheduledValues(now);
            gain.gain.setValueAtTime(cur, now);
            gain.gain.linearRampToValueAtTime(0, now + dur);
            // Schedule el stop + disconnect tras el fade.
            setTimeout(() => {
                try { source.stop(); } catch (e) {}
                try { source.disconnect(); } catch (e) {}
                try { gain.disconnect(); } catch (e) {}
            }, durationMs + 100);
        } catch (e) {}
    }

    // Hooks específicos de eventos del juego — sugar over duck().
    onPlayerDeath() {
        this.duck(0.3, 4000);
    }
    onLevelUp() {
        this.duck(0.6, 2500);
    }
    onWhisperReceived() {
        this.duck(0.5, 1000);
    }
    // boss combat se llama desde el game tick cuando se detecta NPC con
    // maxHp > threshold en el viewport. Mantenelo barato — el caller
    // chequea el flag, no acá.
    onBossCombatStart() {
        this.duck(0.5, 6000);
    }
}

const musicEngine = new MusicEngine();

if (typeof window !== "undefined") {
    window._music = musicEngine; // debug handle
}

export default musicEngine;
